import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, Grid, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { LocaleDateFormat, LocaleFormat, LocaleLanguage, LocaleNumberFormat } from 'models/locale-format';
import { CONSTANTS } from 'config/constants';

const LANGUAGE_OPTIONS: { value: LocaleLanguage; name: string }[] = [
    { value: LocaleLanguage.DA_DK, name: 'Dansk' },
    { value: LocaleLanguage.EN_US, name: 'English' },
    { value: LocaleLanguage.NO_NO, name: 'Norsk' },
    { value: LocaleLanguage.FI_FI, name: 'Suomi' },
    { value: LocaleLanguage.SV_SE, name: 'Svenska' },
];

const NUMBER_FORMAT_OPTIONS: { value: LocaleNumberFormat; labelKey: string; example: string }[] = [
    { value: LocaleNumberFormat.ENGLISH, labelKey: 'NUMBER_FORMAT_ENGLISH', example: '1,234,567.89' },
    { value: LocaleNumberFormat.EUROPEAN, labelKey: 'NUMBER_FORMAT_EUROPEAN', example: '1.234.567,89' },
    { value: LocaleNumberFormat.INDIAN, labelKey: 'NUMBER_FORMAT_INDIAN', example: '12,34,567.89' },
    { value: LocaleNumberFormat.ISO, labelKey: 'NUMBER_FORMAT_ISO', example: '1 234 567,89' },
    { value: LocaleNumberFormat.SWISS, labelKey: 'NUMBER_FORMAT_SWISS', example: "1'234'567.89" },
];

const DATE_FORMAT_OPTIONS: { value: LocaleDateFormat; labelKey: string; example: string }[] = [
    {
        value: LocaleDateFormat.CENTRAL_EUROPE,
        labelKey: 'DATE_FORMAT_CENTRAL_EUROPE',
        example: '31.12.2026 14:30',
    },
    { value: LocaleDateFormat.ISO, labelKey: 'DATE_FORMAT_ISO', example: '2026-12-31 14:30' },
    { value: LocaleDateFormat.US, labelKey: 'DATE_FORMAT_US', example: '12/31/2026 2:30 PM' },
    {
        value: LocaleDateFormat.WESTERN_EUROPE,
        labelKey: 'DATE_FORMAT_WESTERN_EUROPE',
        example: '31/12/2026 14:30',
    },
];

type LocaleFormatPickerProps = {
    value: LocaleFormat;
    onChange: (next: LocaleFormat) => void;
    disabled?: boolean;
};

const LocaleFormatPicker: FC<LocaleFormatPickerProps> = ({ value, onChange, disabled }: LocaleFormatPickerProps) => {
    const { t: translate } = useTranslation(['user-settings']);

    const handleChange = (event: SelectChangeEvent<string>) => {
        onChange({
            ...value,
            [event.target.name]: event.target.value,
        });
    };

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={disabled}>
                    <InputLabel id="locale-format-picker-language-label">
                        {translate('user-settings:LANGUAGE')}
                    </InputLabel>
                    <Select
                        labelId="locale-format-picker-language-label"
                        label={translate('user-settings:LANGUAGE')}
                        value={value.language}
                        onChange={handleChange}
                        name="language"
                    >
                        {LANGUAGE_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {option.name}
                            </MenuItem>
                        ))}
                        {CONSTANTS.DEPLOY_ENV !== 'production' ? (
                            <MenuItem value={LocaleLanguage.DEBUG}>Show translation labels</MenuItem>
                        ) : undefined}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={disabled}>
                    <InputLabel id="locale-format-picker-number-label">
                        {translate('user-settings:NUMBER_FORMAT')}
                    </InputLabel>
                    <Select
                        labelId="locale-format-picker-number-label"
                        label={translate('user-settings:NUMBER_FORMAT')}
                        value={value.number}
                        onChange={handleChange}
                        name="number"
                    >
                        {NUMBER_FORMAT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {translate(`user-settings:${option.labelKey}`)} - {option.example}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={disabled}>
                    <InputLabel id="locale-format-picker-date-label">
                        {translate('user-settings:DATE_FORMAT')}
                    </InputLabel>
                    <Select
                        labelId="locale-format-picker-date-label"
                        label={translate('user-settings:DATE_FORMAT')}
                        value={value.date}
                        onChange={handleChange}
                        name="date"
                    >
                        {DATE_FORMAT_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {translate(`user-settings:${option.labelKey}`)} - {option.example}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
    );
};

export default LocaleFormatPicker;
